/**
 * Avatar — Visual representation of a user, team, or entity.
 *
 * Avatar Component Guidelines
 * - Use `imageSrc` for known users with profile photos, `icon` for abstract entities
 *   (e.g. a workspace or integration), and omit both to fall back to initials from `name`.
 * - Use `isSquare` for non-person entities like workspaces, brands, or apps — round
 *   avatars imply a person.
 * - Size based on context prominence: `xxs`/`xs` for inline mentions or dense lists,
 *   `sm`/`base` for standard list rows and cards, `lg`/`xl` for profile headers or
 *   detail views.
 * - When no `name` or `icon` is provided, a dotted-border placeholder renders — use
 *   this for empty/unassigned states (e.g. "add a member").
 * @see https://base-ui.com/react/components/avatar Base UI Avatar documentation
 * @see https://ui.shadcn.com/docs/components/avatar shadcn/ui Avatar documentation
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import React from "react";

import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import twMerge from "@replo/design-system/utils/twMerge";
import { cva } from "class-variance-authority";

const avatarSizeVariants = cva("", {
  variants: {
    size: {
      xxs: "h-4 w-4",
      xs: "h-5 w-5",
      sm: "h-6 w-6",
      base: "h-9 w-9",
      lg: "h-12 w-12",
      xl: "h-24 w-24",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

const avatarFallbackTextVariants = cva("", {
  variants: {
    size: {
      xxs: "text-xs font-semibold",
      xs: "text-xs",
      sm: "text-sm",
      base: "text-lg",
      lg: "text-lg",
      xl: "text-2xl",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

type AvatarSize = "xxs" | "xs" | "sm" | "base" | "lg" | "xl";

type AvatarProps<ClassName extends string = string> = {
  name?: string | null;
  icon?: React.ReactNode;
  imageSrc?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  size?: AvatarSize;
  imageClassName?: string;
  isSquare?: boolean;
};

function Avatar<ClassName extends string>({
  imageSrc,
  name,
  icon,
  className,
  UNSAFE_className,
  isSquare,
  size = "base",
  imageClassName = "",
}: AvatarProps<ClassName>) {
  const containerClassName = twMerge(
    "inline-flex items-center justify-items-center align-middle shrink-0 text-white",
    isSquare ? "rounded-xs" : "rounded-full",
    avatarSizeVariants({ size }),
    className,
    UNSAFE_className,
  );

  const firstLetter = name?.charAt(0) ?? "?";
  const delay = Boolean(imageSrc) ? 600 : 0;
  return (
    <AvatarPrimitive.Root data-slot="avatar" className={containerClassName}>
      {imageSrc && name && (
        <AvatarPrimitive.Image
          style={{ borderRadius: "inherit" }}
          className={twMerge("h-full w-full object-cover", imageClassName)}
          src={imageSrc}
          alt={name}
        />
      )}
      {name || icon ? (
        <AvatarPrimitive.Fallback
          delay={delay}
          style={{ borderRadius: "inherit" }}
          className={twMerge(
            "bg-white text-default flex h-full w-full items-center justify-center",
            avatarFallbackTextVariants({ size }),
            imageClassName,
          )}
        >
          {icon ?? firstLetter?.toUpperCase()}
        </AvatarPrimitive.Fallback>
      ) : (
        <AvatarPrimitive.Fallback
          delay={delay}
          style={{ borderRadius: "inherit" }}
          className={twMerge(
            "border-border h-full w-full border-2 border-dotted",
            imageClassName,
          )}
        />
      )}
    </AvatarPrimitive.Root>
  );
}

export { Avatar };
export type { AvatarProps, AvatarSize };
